import { useState } from "react";
import { addDays, differenceInCalendarDays, format, startOfDay } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MAX_DAYS = 60;

// Known-length stay: N days = N × 24h from the exact check-in time.
export default function StayLengthPicker({ days, onChange, openEnded = false, onOpenEnded }) {
  const [open, setOpen] = useState(false);
  const [now] = useState(() => new Date());
  const n = Number(days) || 0;
  const endsAt = n > 0 ? new Date(now.getTime() + n * 24 * 3600 * 1000) : null;

  const pick = (date) => {
    if (!date) return;
    const diff = differenceInCalendarDays(startOfDay(date), startOfDay(now));
    onChange(String(Math.min(MAX_DAYS, Math.max(1, diff))));
    setOpen(false);
  };

  return (
    <div className="space-y-2">
      <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-200">
        <input
          data-testid="checkin-open-ended"
          type="checkbox"
          checked={openEnded}
          onChange={(e) => onOpenEnded(e.target.checked)}
          className="h-5 w-5 accent-[#A0148C]"
        />
        Open-ended stay (card on file — auto-charges 1 day every 25h until checkout)
      </label>
      {openEnded ? (
        <p data-testid="checkin-open-ended-note" className="text-xs text-slate-400">
          First day charged now. Next charge {format(new Date(now.getTime() + 25 * 3600 * 1000), "EEE MMM d, h:mm a")}, then every 25 hours.
        </p>
      ) : (<>
      <Label className="text-slate-300">Days</Label>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="checkin-days"
          type="number"
          min={1}
          max={MAX_DAYS}
          value={days}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 2);
            onChange(v === "" ? "" : String(Math.min(MAX_DAYS, Number(v))));
          }}
          className="h-12 w-24 bg-slate-950 border-slate-700 font-data"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="checkin-enddate-trigger"
              className="flex h-12 items-center gap-2 rounded-md border border-slate-700 bg-slate-950 px-4 text-sm text-slate-200 hover:bg-slate-800"
            >
              <CalendarDays className="h-4 w-4 text-[#d946ef]" />
              {endsAt ? `Ends ${format(endsAt, "EEE MMM d")}` : "Pick end date"}
            </button>
          </PopoverTrigger>
          <PopoverContent data-testid="checkin-enddate-calendar" className="w-auto border-slate-700 bg-slate-900 p-0 text-slate-100" align="start">
            <Calendar
              mode="single"
              selected={endsAt || undefined}
              onSelect={pick}
              disabled={{ before: addDays(startOfDay(now), 1), after: addDays(startOfDay(now), MAX_DAYS) }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      <p data-testid="checkin-stay-ends" className="text-xs text-slate-400">
        {endsAt
          ? `Paid through ${format(endsAt, "EEE MMM d, h:mm a")} (${n} × 24h from check-in)`
          : "Enter days or pick an end date, or tick Open-ended."}
      </p>
      </>)}
    </div>
  );
}
